// src/components/common/Sidebar.jsx
import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import {
    HomeIcon, FolderIcon, CurrencyDollarIcon,
    ChartBarIcon, SparklesIcon, Cog6ToothIcon,
    XMarkIcon, ShieldCheckIcon, UserGroupIcon,
    BuildingOffice2Icon, ArrowUpCircleIcon,
} from '@heroicons/react/24/outline';

// ✅ Email platform admin dal .env — mai hardcoded nel codice
const PLATFORM_ADMIN_EMAIL = process.env.REACT_APP_PLATFORM_ADMIN_EMAIL;

const Logo = () => (
    <svg width="32" height="32" viewBox="0 0 100 100" fill="none">
        <path d="M50 5 L90 25 L90 65 Q90 85 50 97 Q10 85 10 65 L10 25 Z" fill="#1d4ed8" />
        <path d="M50 12 L84 30 L84 64 Q84 80 50 91 Q16 80 16 64 L16 30 Z" fill="#3b82f6" />
        <text x="28" y="58" fontFamily="Arial" fontWeight="bold" fontSize="32" fill="white" fontStyle="italic">i</text>
        <text x="42" y="62" fontFamily="Arial" fontWeight="bold" fontSize="36" fill="white">P</text>
    </svg>
);

const PLAN_BADGE = {
    FREE: { label: 'FREE', cls: 'bg-slate-700 text-slate-300' },
    STARTER: { label: 'STARTER', cls: 'bg-blue-800 text-blue-200' },
    PRO: { label: 'PRO', cls: 'bg-purple-800 text-purple-200' },
    ENTERPRISE: { label: 'ENT', cls: 'bg-yellow-700 text-yellow-200' },
};

const Sidebar = ({ open, setOpen }) => {
    const { t } = useTranslation();
    const { user, hasFeature, isSuperAdmin, effectivePlan, can } = useAuth();

    const isPlatformAdmin = PLATFORM_ADMIN_EMAIL && user?.email === PLATFORM_ADMIN_EMAIL;
    const role = user?.role;

    const navigation = [
        // Dashboard — tutti la vedono
        { name: t('nav.dashboard'), href: '/dashboard', icon: HomeIcon, tour: 'dashboard' },
        {
            name: t('nav.projects'), href: '/projects', icon: FolderIcon, tour: 'projects',
            show: isSuperAdmin || can('canManageProjects'),
        },
        {
            name: t('nav.expenses'), href: '/expenses', icon: CurrencyDollarIcon, tour: 'expenses',
            show: isSuperAdmin || can('canManageExpenses') || can('canViewFinancials'),
        },
        {
            name: t('nav.reports'), href: '/reports', icon: ChartBarIcon, tour: 'reports',
            show: isSuperAdmin || can('canViewReports'),
        },
        {
            name: t('nav.aiChat'), href: '/ai-chat', icon: SparklesIcon, tour: 'ai-chat',
            show: (isSuperAdmin || can('canAccessAI')) && (isSuperAdmin || hasFeature('aiEnabled')),
            badge: 'AI',
        },
        {
            name: t('nav.structure'), href: '/organization/structure', icon: BuildingOffice2Icon, tour: 'structure',
            show: isSuperAdmin || role === 'SITE_MANAGER',
        },
        {
            name: t('team.title'), href: '/settings?tab=team', icon: UserGroupIcon, tour: 'team',
            show: isSuperAdmin || can('canManageTeam') || can('canInviteUsers'),
        },
        {
            name: t('nav.upgrade'), href: '/upgrade', icon: ArrowUpCircleIcon,
            show: effectivePlan !== 'ENTERPRISE' && (isSuperAdmin || role === 'SUPER_ADMIN'),
        },
        { name: t('nav.settings'), href: '/settings', icon: Cog6ToothIcon },
        ...(isPlatformAdmin ? [{
            name: t('nav.platformAdmin'), href: '/platform-admin', icon: ShieldCheckIcon, platformAdmin: true,
        }] : []),
    ];

    const filtered = navigation.filter(item => {
        if ('show' in item) return item.show;
        return true;
    });

    const planBadge = PLAN_BADGE[effectivePlan] || { label: effectivePlan, cls: 'bg-slate-700 text-slate-300' };

    return (
        <>
            {open && (
                <div className="fixed inset-0 bg-black/60 z-20 lg:hidden backdrop-blur-sm"
                    onClick={() => setOpen(false)} />
            )}

            <div className={`
                fixed inset-y-0 left-0 z-30 w-64 bg-slate-900 flex flex-col
                transform transition-transform duration-300 ease-in-out
                ${open ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:z-auto
            `}>
                {/* Logo */}
                <div className="flex items-center justify-between h-16 px-4 border-b border-slate-800">
                    <div className="flex items-center gap-2.5">
                        <Logo />
                        <div className="min-w-0">
                            <span className="text-white font-bold text-base leading-none block tracking-tight">InfraPilot</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded font-semibold mt-0.5 inline-block ${planBadge.cls}`}>
                                {isSuperAdmin && '👑 '}{planBadge.label}
                            </span>
                        </div>
                    </div>
                    <button onClick={() => setOpen(false)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition lg:hidden">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* Nav */}
                <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
                    {filtered.map(item => (
                        <NavLink
                            key={item.name}
                            to={item.href}
                            onClick={() => setOpen(false)}
                            data-tour={item.tour || undefined}
                            className={({ isActive }) => `
                                flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                                ${item.platformAdmin ? 'border border-red-700/40 mt-3' : ''}
                                ${isActive
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : item.platformAdmin
                                        ? 'text-red-400 hover:bg-red-900/20 hover:text-red-300'
                                        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                }
                            `}
                        >
                            <item.icon className={`h-5 w-5 flex-shrink-0 ${item.platformAdmin ? 'text-red-400' : ''}`} />
                            <span>{item.name}</span>
                            {item.badge && (
                                <span className="ml-auto text-xs bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded-full font-medium">
                                    {item.badge}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>

                {/* Footer */}
                <div className="p-3 border-t border-slate-800">
                    <div className="flex items-center gap-2.5 px-2 py-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {user?.firstName?.[0]}{user?.lastName?.[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-white truncate">
                                {user?.firstName} {user?.lastName}
                            </p>
                            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                        </div>
                    </div>
                    <p className="text-xs text-slate-600 text-center mt-1">InfraPilot v1.0.0</p>
                </div>
            </div>
        </>
    );
};

export default Sidebar;